import { AlertTriangle, Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { errorMessage } from "@/api/errors";
import { cx } from "@/lib/cx";

import { Button } from "./Button";

export function Spinner({ label = "Loading", className }: { label?: string; className?: string }) {
  return (
    <span role="status" className={cx("inline-flex items-center gap-2 text-sm text-fg-muted", className)}>
      <Loader2 aria-hidden className="size-4 animate-spin" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function PageLoader({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex min-h-40 items-center justify-center">
      <Spinner label={label} />
    </div>
  );
}

/** Table-shaped skeleton that preserves layout while data loads. */
export function SkeletonRows({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div role="status" aria-label="Loading" className="overflow-hidden rounded-lg border border-line bg-surface">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 border-b border-line px-3 py-3 last:border-b-0">
          {Array.from({ length: columns }).map((__, c) => (
            <div
              key={c}
              className="h-3 animate-pulse rounded-sm bg-surface-sunken motion-reduce:animate-none"
              style={{ width: `${c === 0 ? 28 : 14 + ((r + c) % 3) * 6}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  compact,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={cx("flex flex-col items-center justify-center text-center", compact ? "py-8" : "rounded-lg border border-dashed border-line-strong py-14")}>
      {icon && <div className="mb-3 text-fg-subtle [&>svg]:size-6">{icon}</div>}
      <h2 className="text-base font-semibold text-fg">{title}</h2>
      {description && <p className="mt-1 max-w-md text-sm text-fg-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, title = "Couldn't load this" }: { error: unknown; onRetry?: () => void; title?: string }) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center rounded-lg border border-line bg-surface py-12 text-center">
      <AlertTriangle aria-hidden className="mb-2 size-6 text-warning" />
      <h2 className="text-base font-semibold text-fg">{title}</h2>
      <p className="mt-1 max-w-md text-sm text-fg-muted">{errorMessage(error)}</p>
      {onRetry && (
        <Button className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-md border border-danger/30 bg-danger-subtle px-3 py-2 text-sm text-danger-text">
      {errorMessage(error)}
    </div>
  );
}
